/* EPED — connected opening. Progressive enhancement only: every benefit and step is readable
   without this file. Dawn-style custom elements, defined once. */
(() => {
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Design story: three reveals on one whole-bike stage ---------- */
  if (!customElements.get('eped-design-story')) {
    customElements.define('eped-design-story', class extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('.eped-o-stage');
        this.lens = this.querySelector('[data-lens]');
        this.lensLabel = this.querySelector('[data-lens-label]');
        this.line = this.querySelector('[data-line]');
        this.svg = this.querySelector('.eped-o-stage__lines');
        this.items = Array.from(this.querySelectorAll('[data-reveal]'));
        this.markers = Array.from(this.querySelectorAll('[data-reveal-marker]'));
        this.items.forEach((item) => {
          item.querySelector('button').addEventListener('click', () => this.activate(item.dataset.reveal));
          // the magnified detail beside each reveal (below 1100px) selects it too
          const detail = item.querySelector('.eped-o-reveal__detail');
          if (detail) detail.addEventListener('click', () => this.activate(item.dataset.reveal));
        });
        this.markers.forEach((m) => m.addEventListener('click', () => {
          this.activate(m.dataset.revealMarker);
          // on small screens, bring the explanation into view after tapping the bike
          const item = this.items.find((i) => i.dataset.reveal === m.dataset.revealMarker);
          if (item && window.innerWidth < 1100) item.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
        }));
        this.current = (this.items[0] && this.items[0].dataset.reveal) || '1';
        if ('ResizeObserver' in window) { this.ro = new ResizeObserver(() => this.drawLine()); this.ro.observe(this.stage); }
        this.drawLine();
      }
      disconnectedCallback() { if (this.ro) this.ro.disconnect(); }
      activate(n) {
        this.current = n;
        this.items.forEach((i) => {
          const on = i.dataset.reveal === n;
          i.classList.toggle('is-active', on);
          i.querySelector('button').setAttribute('aria-pressed', String(on));
          if (on) {
            this.lens.style.setProperty('--z', i.dataset.z);
            this.lens.style.setProperty('--cx', i.dataset.cx);
            this.lens.style.setProperty('--cy', i.dataset.cy);
            if (this.lensLabel) this.lensLabel.textContent = i.dataset.lensLabel || '';
          }
        });
        this.markers.forEach((m) => m.setAttribute('aria-pressed', String(m.dataset.revealMarker === n)));
        this.drawLine();
      }
      drawLine() {
        if (!this.line || !this.stage) return;
        const marker = this.markers.find((m) => m.dataset.revealMarker === this.current);
        if (!marker) return;
        const s = this.stage.getBoundingClientRect();
        const m = marker.getBoundingClientRect();
        const l = this.lens.getBoundingClientRect();
        this.svg.setAttribute('viewBox', `0 0 ${s.width} ${s.height}`);
        const mx = m.left + m.width / 2 - s.left, my = m.top + m.height / 2 - s.top;
        const lx = l.left + l.width / 2 - s.left, ly = l.top + l.height / 2 - s.top;
        // stop the line at the edge of the lens
        const r = l.width / 2, dx = mx - lx, dy = my - ly, d = Math.hypot(dx, dy) || 1;
        this.line.setAttribute('x1', mx); this.line.setAttribute('y1', my);
        this.line.setAttribute('x2', lx + (dx / d) * r); this.line.setAttribute('y2', ly + (dy / d) * r);
        this.line.classList.add('is-active');
      }
    });
  }

  /* ---------- Proposal: charging, two ways ---------- */
  if (!customElements.get('eped-charge-demo')) {
    customElements.define('eped-charge-demo', class extends HTMLElement {
      connectedCallback() {
        this.fill = this.querySelector('[data-fill]');
        this.label = this.querySelector('[data-level-label]');
        this.buttons = Array.from(this.querySelectorAll('[data-mode]'));
        this.lists = Array.from(this.querySelectorAll('[data-steps]'));
        this.buttons.forEach((b) => b.addEventListener('click', () => this.setMode(b.dataset.mode)));
        this.mode = 'on';
        if (reduced()) { this.finish(); return; }
        this.setLevel(0.25);
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) { io.disconnect(); this.play(); }
        }, { threshold: 0.35 });
        io.observe(this);
      }
      disconnectedCallback() { clearTimeout(this.timer); }
      setMode(mode) {
        this.mode = mode;
        this.buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mode === mode)));
        this.lists.forEach((l) => { l.hidden = l.dataset.steps !== mode; });
        if (reduced()) { this.finish(); return; }
        this.play();
      }
      setLevel(v, animate = false) {
        this.fill.style.setProperty('--level', v);
        if (!this.label) return;
        cancelAnimationFrame(this.raf);
        if (!animate) { this.label.textContent = `${Math.round(v * 100)}%`; return; }
        const from = parseInt(this.label.textContent, 10) || 0, to = Math.round(v * 100), start = performance.now();
        const tick = (t) => {
          const k = Math.min(1, (t - start) / 2400);
          this.label.textContent = `${Math.round(from + (to - from) * (1 - Math.pow(1 - k, 3)))}%`;
          if (k < 1) this.raf = requestAnimationFrame(tick);
        };
        this.raf = requestAnimationFrame(tick);
      }
      finish() {
        this.querySelectorAll('.eped-o-steps li').forEach((li) => li.classList.remove('is-current'));
        this.setLevel(1);
        this.dataset.done = 'true';
      }
      play() {
        clearTimeout(this.timer);
        const steps = Array.from(this.querySelector(`[data-steps="${this.mode}"]`).children);
        this.dataset.done = 'false';
        this.setLevel(0.25);
        let i = 0;
        const next = () => {
          steps.forEach((li, k) => li.classList.toggle('is-current', k === i));
          if (i === steps.length - 1) {
            requestAnimationFrame(() => this.setLevel(1, true));
            this.timer = setTimeout(() => { this.dataset.done = 'true'; }, 2400);
            return;
          }
          i += 1;
          this.timer = setTimeout(next, 1300);
        };
        next();
      }
    });
  }
  /* ---------- Running costs: headline figure and calculator ---------- */
  if (!customElements.get('eped-running-costs')) {
    const LITRES_PER_GALLON = 4.54609;
    const pounds = (p) => { // p in pence
      const v = p / 100;
      if (v >= 100) return '£' + Math.round(v).toLocaleString('en-GB');
      return '£' + v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    // Same rule as the Liquid: nearest 1p under 5p, nearest 5p to £1, nearest 10p above.
    const roundHeadline = (p) => (p < 5 ? Math.round(p) : p < 100 ? Math.round(p / 5) * 5 : Math.round(p / 10) * 10);
    const asPence = (p) => (p < 100 ? `${p}p` : pounds(p));
    const exactPence = (p) => (p < 100 ? `${(Math.round(p * 10) / 10).toLocaleString('en-GB')}p` : pounds(p));
    customElements.define('eped-running-costs', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-calc]');
        if (!this.form) return;
        const f = this.form;
        this.tariffs = Array.from(f.querySelectorAll('input[name="tariff"]'));
        this.tariffs.forEach((r) => r.addEventListener('change', () => { if (f.elements.price) f.elements.price.value = r.value; this.update(); }));
        if (f.elements.price) f.elements.price.addEventListener('input', () => {
          this.tariffs.forEach((r) => { r.checked = parseFloat(r.value) === parseFloat(f.elements.price.value); });
        });
        f.addEventListener('input', () => this.update());
        f.addEventListener('change', () => this.update());
        this.update();
      }
      num(name, fallback) {
        const el = this.form.elements[name];
        if (!el) return fallback;
        const v = parseFloat(el.value);
        return Number.isFinite(v) && v >= 0 ? v : fallback;
      }
      set(key, value) { this.querySelectorAll(`[data-out="${key}"]`).forEach((el) => { el.textContent = value; }); }
      update() {
        const f = this.form;
        const miles = this.num('miles', 40);
        const mpg = Math.max(this.num('mpg', 45), 1);
        const fuel = this.num('fuel', 140);
        const kwh = this.num('kwh', 1.1);
        const range = Math.max(this.num('range', 50), 1);
        const price = this.num('price', 8);
        const mode = (f.elements.mode && f.elements.mode.value) || 'trips';

        const carPerMile = fuel * LITRES_PER_GALLON / mpg; // pence
        const swiftPerMile = kwh * price / range;           // pence
        const per50 = swiftPerMile * 50;
        const yearMiles = miles * 52;
        const carFuelYear = carPerMile * yearMiles;
        const swiftElecYear = swiftPerMile * yearMiles;

        const tariff = this.tariffs.find((r) => r.checked);
        this.set('tariffPhrase', tariff ? tariff.dataset.phrase : `at ${price}p per kWh`);
        this.set('rounded', asPence(roundHeadline(per50)));
        this.set('exact', exactPence(per50));
        this.set('petrol50', pounds(carPerMile * 50));
        this.set('aMpg', String(mpg));
        this.set('aFuel', String(fuel));
        this.set('aKwh', String(kwh));
        this.set('aRange', String(range));
        this.set('aPrice', String(price));
        this.set('miles', String(miles));
        this.set('carFuelYear', pounds(carFuelYear));
        this.set('swiftElecYear', pounds(swiftElecYear));
        this.set('saveFuelYear', pounds(Math.max(carFuelYear - swiftElecYear, 0)));

        const max = Math.max(carFuelYear, swiftElecYear, 1);
        const car = this.querySelector('[data-bar="car"]');
        const swift = this.querySelector('[data-bar="swift"]');
        if (car) car.style.setProperty('--w', `${(carFuelYear / max * 100).toFixed(1)}%`);
        if (swift) swift.style.setProperty('--w', `${(swiftElecYear / max * 100).toFixed(1)}%`);
        const slider = f.elements.miles;
        if (slider) slider.style.setProperty('--fill', `${(slider.value - slider.min) / (slider.max - slider.min) * 100}%`);

        const total = this.querySelector('[data-calc-total]');
        if (total) {
          total.hidden = mode !== 'replace';
          // the replaced car's yearly costs are only asked for when they're used
          const replaceFields = this.querySelector('[data-calc-replace]');
          if (replaceFields) replaceFields.hidden = mode !== 'replace';
          const fixedCar = (this.num('insurance', 0) + this.num('ved', 0) + this.num('service', 0) + this.num('parking', 0)) * 100;
          const carTotal = carFuelYear + fixedCar;
          const swiftTotal = swiftElecYear + this.num('upkeep', 0) * 100;
          this.set('carTotalYear', pounds(carTotal));
          this.set('swiftTotalYear', pounds(swiftTotal));
          this.set('saveTotalYear', pounds(carTotal - swiftTotal));
        }
      }
    });
  }
  /* ---------- Product: build selection and add to basket ---------- */
  if (!customElements.get('eped-product')) {
    customElements.define('eped-product', class extends HTMLElement {
      connectedCallback() {
        try { this.builds = JSON.parse(this.querySelector('[data-builds]').textContent); } catch (e) { return; }
        if (!this.builds.length) return;
        this.form = this.querySelector('[data-eped-form]');
        this.radios = Array.from(this.querySelectorAll('.eped-p-swatch input'));
        this.swatches = Array.from(this.querySelectorAll('.eped-p-swatch'));
        this.configGroup = this.querySelector('[data-config-group]');
        this.configRadios = this.configGroup ? Array.from(this.configGroup.querySelectorAll('input')) : [];
        this.twoLevel = !!this.configGroup && this.builds.some((b) => b.config === 'single') && this.builds.some((b) => b.config === 'two');
        if (this.twoLevel) { this.configGroup.hidden = false; this.classList.add('is-two-level'); }
        this.configRadios.forEach((r) => r.addEventListener('change', () => {
          // keep the colour if that build exists in the other seat layout, else the first available
          const cur = this.byId(this.checkedId());
          const options = this.builds.filter((b) => b.config === r.value);
          const next = options.find((b) => cur && b.colour === cur.colour && b.available) || options.find((b) => b.available) || options[0];
          const radio = this.radios.find((x) => String(x.value) === String(next.id));
          if (radio) radio.checked = true;
          this.show(next, true);
        }));
        this.radios.forEach((r) => r.addEventListener('change', () => this.show(this.byId(r.value), true)));
        if (this.form) this.form.addEventListener('submit', (e) => this.add(e));
        this.show(this.byId(this.checkedId()) || this.builds[0], false);
      }
      checkedId() { const r = this.radios.find((x) => x.checked) || this.radios[0]; return r && r.value; }
      byId(id) { return this.builds.find((b) => String(b.id) === String(id)); }
      show(build, pushUrl) {
        if (!build) return;
        this.dataset.currentConfig = build.config;
        if (this.twoLevel) {
          this.swatches.forEach((sw) => { sw.hidden = sw.dataset.config !== build.config; });
          this.configRadios.forEach((r) => { r.checked = r.value === build.config; });
        }
        const img = this.querySelector('[data-build-image]');
        if (img && img.getAttribute('src') !== build.img) {
          img.classList.add('is-swapping');
          const pre = new Image();
          pre.onload = pre.onerror = () => {
            img.srcset = build.srcset; img.src = build.img; img.alt = build.alt;
            requestAnimationFrame(() => img.classList.remove('is-swapping'));
          };
          pre.src = build.img;
        }
        const note = this.querySelector('[data-build-topbox]');
        if (note) note.hidden = !build.topbox;
        const set = (sel, v) => this.querySelectorAll(sel).forEach((el) => { el.textContent = v; });
        set('[data-build-name]', build.name);
        set('[data-build-note]', build.note);
        set('[data-build-price]', build.price);
        // what's included: mark the rows that apply to the selected seat layout
        this.querySelectorAll('[data-applies]').forEach((row) => {
          const yours = row.dataset.applies === build.config;
          row.classList.toggle('is-yours', yours);
          row.classList.toggle('is-other', !yours);
          const tag = row.querySelector('.eped-p-inc__yours');
          if (tag) tag.hidden = !yours;
        });
        const btn = this.querySelector('[data-add]');
        if (btn) {
          btn.disabled = !build.available;
          const label = btn.querySelector('[data-add-label]');
          if (label) label.textContent = build.available ? btn.dataset.addText : btn.dataset.soldText;
        }
        this.querySelectorAll('[data-build-link]').forEach((a) => { a.href = build.url; });
        const status = this.querySelector('[data-add-status]');
        if (status && pushUrl) status.replaceChildren();
        if (pushUrl && this.form) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', build.id);
          window.history.replaceState({}, '', url.toString());
        }
      }
      async add(e) {
        if (!window.fetch || !window.FormData) return; // native post to the basket
        e.preventDefault();
        const btn = this.querySelector('[data-add]');
        const status = this.querySelector('[data-add-status]');
        const build = this.byId(this.checkedId());
        if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
        status.replaceChildren();
        status.classList.remove('is-error');
        try {
          const res = await fetch(`${this.dataset.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: new FormData(this.form) });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.description || data.message || '');
          const cart = await (await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
          this.updateBasket(cart);
          const strong = document.createElement('strong'); strong.textContent = build ? build.name : '';
          const link = document.createElement('a'); link.href = this.dataset.cartUrl; link.textContent = this.dataset.view;
          status.append(`${this.dataset.added} `, strong, '. ', link);
        } catch (err) {
          status.classList.add('is-error');
          status.append(this.dataset.error, err.message ? ` (${err.message})` : '');
        } finally {
          if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = !(build && build.available); }
        }
      }
      updateBasket(cart) {
        // Dawn: re-render its basket icon through the Section Rendering API
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && bubble.hasAttribute('data-eped-draft-basket')) {
          bubble.setAttribute('aria-label', `Basket, ${cart.item_count} items`);
          const count = bubble.querySelector('.cart-count-bubble');
          if (count) count.textContent = cart.item_count;
        } else if (bubble) {
          fetch(`${window.location.pathname}?sections=cart-icon-bubble`).then((r) => r.json()).then((j) => {
            const html = j && j['cart-icon-bubble'];
            if (!html) return;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const inner = doc.querySelector('.shopify-section');
            bubble.innerHTML = inner ? inner.innerHTML : html;
          }).catch(() => {});
        }
        // anything else that shows the basket count (the local preview's header) listens for this
        document.dispatchEvent(new CustomEvent('eped:cart-updated', { detail: { item_count: cart.item_count } }));
      }
    });
  }
  /* ---------- Home: animate the route line and the battery fill as they come into view ---------- */
  const inview = Array.from(document.querySelectorAll('[data-eped-inview]:not(.is-in)'));
  if (inview.length && 'IntersectionObserver' in window && !reduced()) {
    inview.forEach((el) => el.classList.add('is-pending'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.remove('is-pending'); e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { threshold: 0.25 });
    inview.forEach((el) => io.observe(el));
  }
  /* ---------- Contact: ?topic=… preselects the enquiry topic ---------- */
  const topicSelect = document.querySelector('.eped-c [data-topic-from-url]');
  if (topicSelect) {
    const topic = new URLSearchParams(window.location.search).get('topic');
    const opt = topic && Array.from(topicSelect.options).find((o) => o.value.toLowerCase() === topic.toLowerCase());
    if (opt) topicSelect.value = opt.value;
  }
  const sent = document.querySelector('.eped-c__notice[tabindex="-1"]');
  if (sent) sent.focus();
  /* ---------- Links such as #charging open the matching answer ---------- */
  const openTarget = () => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    const el = id && document.getElementById(id);
    if (el && el.matches('details.eped-p-disc')) el.open = true;
  };
  openTarget();
  window.addEventListener('hashchange', openTarget);
})();

// Keep the draft navigation in sync with Dawn's native quantity and removal events.
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => {
    const basket = document.querySelector('[data-eped-draft-basket]');
    if (!basket || !cartData || typeof cartData.item_count !== 'number') return;
    basket.setAttribute('aria-label', `Basket, ${cartData.item_count} items`);
    const count = basket.querySelector('.cart-count-bubble');
    if (count) count.textContent = cartData.item_count;
  });
}
